// components/EligibilityModal.jsx
import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import '../../components/EligibilityModal.css';

const CapmEligibilityModal = ({ isOpen, onClose, onComplete }) => {
  // Réinitialiser tous les états à leur valeur par défaut
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // État pour les alertes personnalisées
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [formData, setFormData] = useState({
    // Étape 1: Niveau académique
    niveauAcademique: '',

    // Étape 2: Expérience
    experienceProjet: '',

    // Étape 3: QCMs PMP (regroupés)
    qcm1: '',
    qcm2: '',
    qcm3: '',
    qcm4: ''
  });

  const [scoreQCM, setScoreQCM] = useState(0);

  // Questions du questionnaire (3 étapes seulement)
  const questions = [
    {
      id: 1,
      title: "Niveau académique",
      description: "Quel est votre niveau académique le plus élevé ?",
      type: 'simple',
      field: {
        name: 'niveauAcademique',
        type: 'select',
        options: [
          { value: '', label: 'Sélectionnez votre niveau' },
          { value: 'inf_bac3', label: 'Inférieur à BAC+3 (BAC, BTS, DUT, Licence 1/2)' },
          { value: 'sup_bac3', label: 'Supérieur ou égal à BAC+3 (Licence 3, Master, Doctorat, Ingénieur)' }
        ],
        required: true
      }
    },
    {
      id: 2,
      title: "Expérience en gestion de projet",
      description: "Combien d'années d'expérience avez-vous en tant qu'acteur ou partie prenante de projet ?",
      type: 'simple',
      field: {
        name: 'experienceProjet',
        type: 'select',
        options: [
          { value: '', label: 'Sélectionnez votre expérience' },
          { value: 'moins3ans', label: 'Inférieur à 3 ans' },
          { value: '3a5ans', label: 'Entre 3 ans et 5 ans' },
          { value: 'plus5ans', label: 'Supérieur ou égal à 5 ans' }
        ],
        required: true
      }
    },
    {
      id: 3,
      title: "Questionnaire CAPM",
      description: "Répondez aux questions suivantes pour évaluer vos connaissances en gestion de projet :",
      type: 'qcm',
      questions: [
        {
          id: 'qcm1',
          text: "Lesquels des paramètres suivants sont à prendre en compte et à surveiller en gestion de projet ?",
          options: [
            { value: 'a', label: "A. Le périmètre, le délai et les coûts", points: 0 },
            { value: 'b', label: "B. La qualité les ressources, les communications", points: 0 },
            { value: 'c', label: "C. Les risques, les parties prenantes et les approvisionnements", points: 0 },
            { value: 'd', label: "D. Tous", points: 25 }
          ],
          correctAnswer: 'd',
          explanation: ""
        },
        {
          id: 'qcm2',
          text: "La gestion de projet est :",
          options: [
            { value: 'a', label: "A. L'intégration de la méthode du chemin critique et du système de gestion de la valeur acquise", points: 0 },
            { value: 'b', label: "B. L'application des connaissances, des compétences, des outils et des techniques aux activités du projet afin de répondre aux exigences du projet", points: 25 },
            { value: 'c', label: "C. L'application des connaissances, des compétences, de la sagesse, de la science et de l'art aux activités organisationnelles pour atteindre l'excellence opérationnelle", points: 0 },
            { value: 'd', label: "D. Un sous-ensemble de la plupart des disciplines techniques et d'ingénierie.", points: 0 }
          ],
          correctAnswer: 'b',
          explanation: ""
        },
        {
          id: 'qcm3',
          text: "Si vous influencez les facteurs qui créent les changements de coût du projet, quel processus utilisez-vous ?",
          options: [
            { value: 'a', label: "A. Négocier les coûts", points: 0 },
            { value: 'b', label: "B. Estimer les coûts", points: 0 },
            { value: 'c', label: "C. Maîtriser les coûts", points: 25 },
            { value: 'd', label: "D. Déterminer le budget", points: 0 }
          ],
          correctAnswer: 'c',
          explanation: ""
        },
        {
          id: 'qcm4',
          text: "Qui doit signer une charte de projet ?",
          options: [
            { value: 'a', label: "A. Le Chef d’Équipe", points: 0 },
            { value: 'b', label: "B. Le Sponsor", points: 25 },
            { value: 'c', label: "C. Le DG", points: 0 },
            { value: 'd', label: "D. Le Chef de Projet", points: 0 }
          ],
          correctAnswer: 'b',
          explanation: ""
        }
      ]
    }
  ];

  // Effet pour réinitialiser complètement quand le modal est fermé
  useEffect(() => {
    if (!isOpen) {
      // Réinitialiser tous les états à zéro
      setCurrentStep(1);
      setShowResults(false);
      setShowConfetti(false);
      setFormData({
        niveauAcademique: '',
        experienceProjet: '',
        qcm1: '',
        qcm2: '',
        qcm3: '',
        qcm4: ''
      });
      setScoreQCM(0);
      setShowAlert(false);
      setAlertMessage('');
    }
  }, [isOpen]); // Cet effet s'exécute chaque fois que isOpen change

  // Effet pour gérer la taille de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effet pour arrêter les confettis après 5 secondes
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000); // 5 secondes

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Effet pour fermer automatiquement l'alerte après 3 secondes
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  // Fonction pour afficher une alerte personnalisée
  const showCustomAlert = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  // Fonction pour fermer le modal et réinitialiser
  const handleCloseModal = () => {
    // Appeler la fonction onClose fournie par le parent
    onClose();

    // Les états seront réinitialisés par l'effet ci-dessus
    // car isOpen deviendra false
  };

  // Calculer le score QCM
  const calculateQCMScore = () => {
    let score = 0;

    if (formData.qcm1 === 'd') score += 25;
    if (formData.qcm2 === 'b') score += 25;
    if (formData.qcm3 === 'c') score += 25;
    if (formData.qcm4 === 'b') score += 25;

    setScoreQCM(score);
    return score;
  };

  // Vérifier si toutes les questions QCM sont répondues
  const areAllQCMsAnswered = () => {
    return formData.qcm1 && formData.qcm2 && formData.qcm3 && formData.qcm4;
  };

  // Déterminer la recommandation finale - CORRIGÉ
  const getRecommendation = () => {
    const niveau = formData.niveauAcademique;
    const experience = formData.experienceProjet;

    // Règles de recommandation - ORDRE IMPORTANT !

    // 1. CAS SPÉCIAL : Niveau inférieur mais expérience 5+ ans → PMP
    if (niveau === 'inf_bac3' && experience === 'plus5ans') {
      return {
        certification: 'PMP',
        reason: 'Nous vous recommandons la certification PMP. Bien que votre niveau académique soit inférieur à BAC+3, votre expérience substantielle (5 ans ou plus) vous rend éligible pour cette certification.'
      };
    }

    // 2. Niveau inférieur à BAC+3 mais expérience entre 3 à 5 ans → PMP
    if (niveau === 'inf_bac3' && experience === '3a5ans') {
      return {
        certification: 'PMP',
        reason: 'Nous vous recommandons la certification PMP. Avec un niveau (inférieur à BAC+3) et une expérience (comprise entre 3 et 5 ans) vous êtes éligible pour cette certification avancée.'
      };
    }

    // 3. Niveau BAC+3+ mais moins de 3 ans d'expérience → PMP
    if (niveau === 'sup_bac3' && experience === '3a5ans') {
      return {
        certification: 'PMP',
        reason: 'Nous vous recommandons la certification PMP. Avec un niveau (supérieur à BAC+3) et une expérience (comprise entre 3 et 5 ans) vous êtes éligible pour cette certification avancée.'
      };
    }

    // 4. Niveau BAC+3+ mais moins de 3 ans d'expérience → PMP
    if (niveau === 'sup_bac3' && experience === 'plus5ans') {
      return {
        certification: 'PMP',
        reason: 'Nous vous recommandons la certification PMP. Votre profil (supérieur à BAC+3 avec plus de 5 ans d\'expérience) vous rend éligible pour cette certification.'
      };
    }

    // 5. Tous les autres cas → PMP
    return {
      certification: 'CAPM',
      reason: 'Nous vous recommandons la certification CAPM. Votre profil correspond aux critères d\'éligibilité.  Vous êtes qualifié pour cette certification d\'introduction à la gestion de projet.'
    };
  };

  // Évaluer le score QCM
  const getQCMEvaluation = (score) => {
    if (score === 0) return { niveau: 'Faible', couleur: '#ef4444' };
    if (score <= 25) return { niveau: 'Faible', couleur: '#ef4444' };
    if (score <= 50) return { niveau: 'Passable', couleur: '#f59e0b' };
    if (score <= 75) return { niveau: 'Assez bien', couleur: '#10b981' };
    return { niveau: 'Très bien', couleur: '#10b981' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    // Validation de l'étape courante
    const currentQuestion = questions[currentStep - 1];

    if (currentQuestion.type === 'simple') {
      if (!formData[currentQuestion.field.name]) {
        showCustomAlert('Veuillez sélectionner une réponse avant de continuer.');
        return;
      }
    }

    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    // Vérifier que toutes les questions QCM sont répondues
    if (!areAllQCMsAnswered()) {
      showCustomAlert('Veuillez répondre à toutes les questions avant de terminer.');
      return;
    }

    // Calculer le score et afficher les résultats
    const calculatedScore = calculateQCMScore(); // Récupérer le score calculé
    setShowResults(true);
    setShowConfetti(true); // Lancer les confettis

    // ENREGISTRER LES DONNÉES DANS LE LOCALSTORAGE DÈS MAINTENANT
    const recommendation = getRecommendation();
    const qcmEvaluation = getQCMEvaluation(calculatedScore); // Utiliser le score calculé

    // console.log('handleFinish - Score calculé:', calculatedScore);
    // console.log('handleFinish - Recommendation:', recommendation.certification);

    // ENREGISTRER DANS LE LOCALSTORAGE POUR TOUS LES CAS (PMP ET CAPM)
    const scoreData = {
      score: calculatedScore, // Utiliser calculatedScore au lieu de scoreQCM
      niveau: qcmEvaluation.niveau,
      date: new Date().toISOString(),
      recommendation: recommendation.certification,
      formData: formData
    };

    localStorage.setItem('capm_eligibility_score', JSON.stringify(scoreData));
    // console.log('Score enregistré dans localStorage (pour PMP ou CAPM):', scoreData);

    // Vérifier que c'est bien enregistré
    const savedData = localStorage.getItem('capm_eligibility_score');
    // console.log('Vérification localStorage:', savedData);
  };

  // Puis modifie handleSubmit pour ne PAS enregistrer à nouveau
  const handleSubmit = () => {
    const recommendation = getRecommendation();
    const qcmEvaluation = getQCMEvaluation(scoreQCM);

    if (onComplete) {
      onComplete({
        ...formData,
        scoreQCM,
        qcmEvaluation,
        recommendation
      });
    }

    // 🔥 VÉRIFIER S'IL Y A UNE SESSION CALENDRIER EN ATTENTE
    const pendingReference = localStorage.getItem('pending_calendar_reference');
    const pendingSession = localStorage.getItem('pending_calendar_session');
    const pendingCertification = localStorage.getItem('pending_calendar_certification');

    // Fermer le modal
    handleCloseModal();

    if (pendingReference && pendingSession) {
      // 🔥 REDIRIGER VERS L'INSCRIPTION AVEC LES DONNÉES DU CALENDRIER
      try {
        const sessionData = JSON.parse(pendingSession);

        // Nettoyer le localStorage
        localStorage.removeItem('pending_calendar_session');
        localStorage.removeItem('pending_calendar_reference');
        localStorage.removeItem('pending_calendar_certification');

        // Rediriger vers l'inscription
        setTimeout(() => {
          navigate('/inscription', {
            state: {
              formationReference: pendingReference,
              sessionData: sessionData,
              eligibilityCompleted: true,
              certificationType: pendingCertification || "CAPM",
              fromCalendar: true
            }
          });
        }, 300);
        return;
      } catch (error) {
        console.error("Erreur lors de la lecture de la session:", error);
        // Nettoyer en cas d'erreur
        localStorage.removeItem('pending_calendar_session');
        localStorage.removeItem('pending_calendar_reference');
        localStorage.removeItem('pending_calendar_certification');
      }
    }

    // 🔥 COMPORTEMENT NORMAL (si pas de session en attente)
    // Si CAPM, scroller vers la section calendrier
    if (recommendation.certification === 'CAPM') {
      setTimeout(() => {
        const calendarSection = document.getElementById('calendrier');
        if (calendarSection) {
          calendarSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          window.scrollTo({
            top: document.body.scrollHeight * 0.6,
            behavior: 'smooth'
          });
        }
      }, 300);
    }

    // Si PMP, scroller vers la section contact
    if (recommendation.certification === 'PMP') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  // Afficher les résultats si on a cliqué sur "Terminer"
  const isLastStep = currentStep === questions.length;
  const currentQuestion = questions[currentStep - 1];
  const recommendation = getRecommendation();
  const qcmEvaluation = getQCMEvaluation(scoreQCM);

  // Vérifier si toutes les QCM sont répondues pour l'étape 3
  const allQCMsAnswered = areAllQCMsAnswered();

  if (!isOpen) return null;

  return (
    <>
      {/* Alerte personnalisée */}
      {showAlert && (
        <div className="custom-alert">
          <div className="alert-content">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#f59e0b" />
              <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>{alertMessage}</span>
          </div>
        </div>
      )}

      {/* Confettis qui apparaissent quand showConfetti est true */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={true}
          numberOfPieces={200}
          gravity={0.1}
          colors={['#0a3764', '#94bf3e', '#10b981', '#f59e0b', '#ef4444']}
          style={{ position: 'fixed', zIndex: 1001 }}
        />
      )}

      <div className="eligibility-modal-overlay">
        <div className="eligibility-modal">
          {/* En-tête de la modal */}
          <div className="modal-header">
            <div className="header-content">
              <h2 className="modal-title">Questionnaire d'Éligibilité</h2>
            </div>

            <button className="close-btn" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Contenu de la modal */}
          <div className="modal-content">
            {!showResults ? (
              // Étape normale du questionnaire (étapes 1, 2, 3)
              <>
                <div className="question-header">
                  <h3>{currentQuestion.description}</h3>
                </div>

                <div className="form-fields">
                  {currentQuestion.type === 'simple' ? (
                    <div className="form-field">
                      <label className="field-label">
                        Sélectionnez votre réponse
                        <span className="required">*</span>
                      </label>

                      <select
                        name={currentQuestion.field.name}
                        value={formData[currentQuestion.field.name] || ''}
                        onChange={handleChange}
                        className="form-input"
                        required={currentQuestion.field.required}
                      >
                        {currentQuestion.field.options.map((option, optIndex) => (
                          <option key={optIndex} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    // QCMs regroupés (étape 3)
                    <div className="qcm-container">
                      {currentQuestion.questions.map((qcm, qcmIndex) => (
                        <div key={qcm.id} className="qcm-group">
                          {/* Mettre le numéro et la question sur la même ligne */}
                          <div className="qcm-header">
                            <h4 className="qcm-title">{qcmIndex + 1}.</h4>
                            <p className="qcm-text">{qcm.text}</p>
                          </div>

                          <div className="qcm-options">
                            {qcm.options.map((option, optIndex) => (
                              <div key={optIndex} className="qcm-option">
                                <input
                                  type="radio"
                                  id={`${qcm.id}_${option.value}`}
                                  name={qcm.id}
                                  value={option.value}
                                  checked={formData[qcm.id] === option.value}
                                  onChange={handleChange}
                                  className="qcm-radio"
                                />
                                <label
                                  htmlFor={`${qcm.id}_${option.value}`}
                                  className="qcm-label"
                                >
                                  <span className="qcm-text">{option.label}</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Résultats de l'évaluation (après avoir cliqué sur "Terminer" ou chargement depuis localStorage)
              <>
                <div className="eligibility-results">
                  {/* Score QCM */}
                  <div className="score-card">
                    <div className="score-header">
                      <h4>Score</h4>
                      <div className="score-badge" style={{ backgroundColor: qcmEvaluation.couleur }}>
                        {scoreQCM}/100
                      </div>
                    </div>
                    <p className="score-description">
                      Niveau : <strong>{qcmEvaluation.niveau}</strong>
                    </p>

                    <div className="score-bar-container">
                      <div className="score-bar">
                        <div
                          className="score-fill"
                          style={{
                            width: `${scoreQCM}%`,
                            backgroundColor: qcmEvaluation.couleur
                          }}
                        ></div>
                      </div>
                      <div className="score-legend">
                        <span>0</span>
                        <span>25 (Faible)</span>
                        <span>50 (Passable)</span>
                        <span>75 (Assez bien)</span>
                        <span>100 (Très bien)</span>
                      </div>
                    </div>

                    {/* Icône de succès verte centrée */}
                    <div className="text-center mt-4 ">
                      <div className="mb-3">
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="12" fill="#10b981" />
                          <path d="M10 15L7 12L5.5 13.5L10 18L18.5 9.5L17 8L10 15Z" fill="white" />
                        </svg>
                      </div>

                      {/* Message de recommandation simple */}
                      <div className="recommendation-message">
                        <p style={{
                          color: '#4b5563',
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          margin: '0'
                        }}>
                          {recommendation.reason}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer de la modal avec les boutons */}
          <div className="modal-footer">
            <div className="footer-actions">
              {!showResults ? (
                <div className="actions-container">
                  {currentStep > 1 ? (
                    <>
                      <button className="btn-secondary" onClick={handlePrev}>
                        Précédent
                      </button>

                      <button
                        className={`${isLastStep && allQCMsAnswered ? 'btn-success' : 'btn-primary'}`}
                        onClick={isLastStep ? handleFinish : handleNext}
                        disabled={isLastStep && !allQCMsAnswered}
                      >
                        {isLastStep ? 'Terminer' : 'Suivant'}
                      </button>
                    </>
                  ) : (
                    // À l'étape 1, seulement le bouton Suivant aligné à droite
                    <div className="step1-actions">
                      <button
                        className="btn-primary"
                        onClick={handleNext}
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // SEULEMENT le bouton d'inscription centré
                <div className="actions-container" style={{ justifyContent: 'center' }}>
                  <button className="btn-success" onClick={handleSubmit} style={{ margin: '0 auto' }}>
                    {recommendation.certification === 'CAPM'
                      ? 'S\'inscrire à la prochaine session CAPM'
                      : 'Découvrir la formation PMP'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <style jsx>{`
          /* Alerte personnalisée */
          .custom-alert {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #f59e0b;
            color: white;
            padding: 12px 18px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1002;
            animation: slideIn 0.3s ease-out;
            max-width: 400px;
            min-width: 300px;
          }
          
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOut {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
          
          .custom-alert.fade-out {
            animation: slideOut 0.3s ease-out forwards;
          }
          
          .alert-content {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          
          .alert-content svg {
            flex-shrink: 0;
          }
          
          .alert-content span {
            font-size: 0.95rem;
            font-weight: 500;
            line-height: 1.4;
          }

          /* Overlay */
          .eligibility-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            padding: 18px;
          }

          /* Modal principale */
          .eligibility-modal {
            background: white;
            border-radius: 10px;
            width: 100%;
            
            max-height: 95vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 18px 54px rgba(0, 0, 0, 0.25);
            overflow: hidden;
          }

          /* En-tête */
          .modal-header {
            background: linear-gradient(135deg, #0a3764 0%, #09407e 100%);
            color: white;
            padding: 1.3rem 1.8rem;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            position: relative;
          }

          .header-content {
            flex: 1;
            display: flex;
            align-items: center;
          }

          .modal-title {
            margin: 0;
            font-size: 1.6rem;
            line-height: 1.2;
          }

          .subtitle {
            margin: 0 0 1.3rem 0;
            opacity: 0.9;
            font-size: 0.9rem;
          }

          /* Bouton fermer */
          .close-btn {
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: 0.4rem;
            border-radius: 3px;
            transition: background 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          .close-btn svg {
            width: 20px;
            height: 20px;
          }

          /* Contenu */
          .modal-content {
            flex: 1;
            overflow-y: scroll;
            padding: 0.8rem 1.8rem;
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }

          /* Pour Chrome, Safari et Edge */
          .modal-content::-webkit-scrollbar {
            width: 6px;
          }

          .modal-content::-webkit-scrollbar-track {
            background: #f1f1f6;
            border-radius: 3px;
          }

          .modal-content::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }

          .modal-content::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }

          /* Question header */
          .question-header {
            margin-bottom: 1.8rem;
          }

          .question-header h3 {
            color: '#0a3764';
            margin-bottom: 0.4rem;
            font-size: 1.3rem;
            text-align: left;
          }

          .question-header p {
            color: '#666';
            font-size: 0.95rem;
          }

          /* QCM Container */
          .qcm-container {
            display: flex;
            flex-direction: column;
            gap: 1.8rem;
          }

          .qcm-group {
            
          }

          .qcm-group:last-child {
            margin-bottom: 0;
          }

          /* Header avec numéro et question sur la même ligne */
          .qcm-header {
            display: flex;
            align-items: flex-start;
            gap: 0.4rem;
            margin-bottom: 0.8rem;
          }

          .qcm-title {
            color: '#0a3764';
            font-size: 1.1rem;
            margin: 0;
            line-height: 1.4;
            min-width: 1.3rem;
            flex-shrink: 0;
          }

          .qcm-text {
            color: '#666';
            font-size: 0.95rem;
            line-height: 1.4;
            margin: 0;
            flex: 1;
          }

          /* QCM Options - 2 par ligne */
          .qcm-options {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.8rem;
          }

          .qcm-option {
            display: flex;
            align-items: flex-start;
            padding: 0.8rem;
            border: 1px solid '#e5e7eb';
            border-radius: 6px;
            transition: all 0.2s;
            cursor: pointer;
            height: 100%;
          }

          .qcm-option:hover {
            border-color: '#0a3764';
            background: '#f0f7ff';
          }

          .qcm-radio {
            margin-right: 0.75rem;
            width: 18px;
            height: 18px;
            margin-top: 0.125rem;
            flex-shrink: 0;
          }

          .qcm-label {
            display: flex;
            align-items: flex-start;
            cursor: pointer;
            width: 100%;
            height: 100%;
          }

          /* Pour les options QCM à l'intérieur */
          .qcm-option .qcm-text {
            color: '#374151';
            font-size: 0.9rem;
            line-height: 1.3;
            display: flex;
            align-items: center;
            min-height: 100%;
          }

          /* Form fields */
          .form-field {
            margin-bottom: 1.5rem;
          }

          .field-label {
            display: block;
            margin-bottom: 0.5rem;
            color: '#374151';
            font-weight: 500;
            font-size: 0.9rem;
          }

          .required {
            color: '#ef4444';
            margin-left: 2px;
          }

          .form-input {
            width: 100%;
            padding: 0.7rem 0.9rem;
            border: 1px solid '#d1d5db';
            border-radius: 5px;
            font-size: 0.9rem;
            transition: border-color 0.2s;
          }

          .form-input:focus {
            outline: none;
            border-color: '#0a3764';
            box-shadow: 0 0 0 2px rgba(10, 55, 100, 0.1);
          }

          /* Footer */
          .modal-footer {
            background: '#f9fafb';
            border-top: 1px solid '#e5e7eb';
          }

          .footer-actions {
            width: 100%;
          }

          .actions-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            gap: 0.8rem;
          }

          .step1-actions {
            display: flex;
            justify-content: flex-end;
            width: 100%;
          }

          /* Score Card */
          .score-card {
            background: white;
            border-radius: 8px;
            padding: 1.3rem;
          }

          .score-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.8rem;
          }

          .score-header h4 {
            font-size: 1.1rem;
            color: '#0a3764';
            margin: 0;
          }

          .score-badge {
            padding: 0.4rem 0.9rem;
            border-radius: 18px;
            color: white;
            font-weight: bold;
            font-size: 0.95rem;
          }

          .score-description {
            margin-bottom: 1.3rem;
            color: '#4b5563';
            font-size: 0.9rem;
          }

          .score-bar-container {
            margin-bottom: 1.3rem;
          }

          .score-bar {
            height: 10px;
            background: '#f3f4f6';
            border-radius: 5px;
            overflow: hidden;
            margin-bottom: 0.4rem;
          }

          .score-fill {
            height: 100%;
            border-radius: 5px;
            transition: width 0.3s ease;
          }

          .score-legend {
            display: flex;
            justify-content: space-between;
            font-size: 0.7rem;
            color: '#6b7280';
          }

          .score-details h5 {
            margin-bottom: 0.8rem;
            color: '#0a3764';
            font-size: 1rem;
          }

          .qcm-details-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.8rem;
          }

          .qcm-detail {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
          }

          .qcm-status {
            font-size: 0.85rem;
            font-weight: bold;
          }

          .correct {
            color: '#10b981';
          }

          .incorrect {
            color: '#ef4444';
          }

          .qcm-question {
            font-size: 0.85rem;
            color: '#6b7280';
          }

          /* Results header */
          .results-header {
            margin-bottom: 1.5rem;
          }

          .results-header h3 {
            color: '#0a3764';
            margin-bottom: 0.4rem;
            font-size: 1.3rem;
          }

          .results-header p {
            color: '#666';
            font-size: 0.95rem;
          }

          /* Boutons */
          .btn-primary, .btn-secondary, .btn-success {
            padding: 0.7rem 1.3rem;
            border-radius: 5px;
            font-weight: 600;
            font-size: 0.9rem;
            cursor: pointer;
            border: none;
            transition: all 0.2s;
            min-width: 118px;
            width: auto;
          }

          .btn-primary {
            background: '#0a3764';
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background: '#09407e';
            transform: translateY(-1px);
          }

          .btn-secondary {
            background: '#7e7e7eff';
            color: '#ffffffff';
          }

          .btn-secondary:hover {
            background: '#e5e7eb';
          }

          .btn-success {
            background: '#94bf3e';
            color: white;
          }

          .btn-success:hover {
            background: '#83a935';
            transform: translateY(-1px);
          }

          .btn-primary:disabled,
          .btn-success:disabled {
            background: '#d1d5db';
            color: '#6b7280';
            cursor: not-allowed;
            opacity: 0.6;
            transform: none;
          }

          /* Styles pour les résultats */
          .text-center {
            text-align: center !important;
          }

          .mt-4 {
            margin-top: 1.5rem !important;
          }

          .mb-3 {
            margin-bottom: 1rem !important;
          }

          .mb-4 {
            margin-bottom: 1.5rem !important;
          }

          .recommendation-message {
            max-width: 800px;
            margin: 0 auto;
            padding: 0 1rem;
          }

          /* Responsive */
          @media (max-width: 768px) {
            /* Alerte personnalisée mobile */
            .custom-alert {
              top: 10px;
              right: 10px;
              left: 10px;
              max-width: none;
              min-width: auto;
            }
            
            .alert-content {
              gap: 10px;
            }
            
            .alert-content span {
              font-size: 0.9rem;
            }
            
            /* Modal overlay mobile */
            .eligibility-modal-overlay {
              padding: 10px;
            }

            /* Modal header mobile */
            .modal-header {
              padding: 1rem 1.2rem;
              align-items: center;
              min-height: 60px;
            }
            
            .modal-title {
              font-size: 1.3rem; /* Taille réduite du titre sur mobile */
              line-height: 1.1;
            }
            
            .close-btn {
              padding: 0.3rem;
            }
            
            .close-btn svg {
              width: 18px;
              height: 18px;
            }

            /* Modal content mobile */
            .modal-content {
              padding: 1rem 1.2rem;
            }

            .modal-footer {
              padding: 0.8rem 1rem;
            }

            /* QCM mobile */
            .qcm-options {
              grid-template-columns: 1fr;
              gap: 0.6rem;
            }

            .qcm-details-grid {
              grid-template-columns: 1fr;
              gap: 0.6rem;
            }

            /* Score header mobile */
            .score-header {
              flex-direction: row;
              align-items: center;
              gap: 0.8rem;
            }

            .score-header h4 {
              font-size: 1rem;
            }
            
            .score-badge {
              padding: 0.3rem 0.7rem;
              font-size: 0.9rem;
            }

            /* Actions container mobile */
            .actions-container {
              flex-direction: column;
              gap: 0.8rem;
            }

            .step1-actions {
              justify-content: center;
            }

            .btn-primary, .btn-secondary, .btn-success {
              width: 100%;
              max-width: 100%;
              padding: 0.8rem 1rem;
              font-size: 0.9rem;
            }

            /* QCM header mobile */
            .qcm-header {
              flex-direction: row;
              gap: 0.4rem;
              align-items: flex-start;
            }
            
            .qcm-title, .qcm-text {
              width: auto;
            }
            
            .qcm-option {
              padding: 0.7rem;
            }

            /* Question header mobile */
            .question-header h3 {
              font-size: 1.1rem;
            }

            /* Recommendation message mobile */
            .recommendation-message {
              padding: 0 0.5rem;
            }
            
            .recommendation-message p {
              font-size: 0.9rem;
            }
          }

          @media (min-width: 769px) and (max-width: 1024px) {
            .qcm-options {
              gap: 0.6rem;
            }
            
            .qcm-option {
              padding: 0.7rem;
            }
            
            .modal-title {
              font-size: 1.4rem;
            }
          }

          /* Très petits écrans */
          @media (max-width: 480px) {
            .modal-title {
              font-size: 1.2rem;
            }
            
            .close-btn {
              padding: 0.2rem;
            }
            
            .close-btn svg {
              width: 16px;
              height: 16px;
            }
            
            .modal-header {
              padding: 0.8rem 1rem;
            }
            
            .question-header h3 {
              font-size: 1rem;
            }
            
            .score-badge {
              padding: 0.2rem 0.6rem;
              font-size: 0.85rem;
            }
          }

          .recommendation-card {
            padding: 1rem;
            border-radius: 6px;
            margin-top: 0.5rem;
          }

          .recommendation-reason {
            color: '#4b5563';
            font-size: 0.9rem;
            line-height: 1.5;
          }

          .profile-summary {
            background: '#f8fafc';
            padding: 0.8rem;
            border-radius: 6px;
            margin-top: 0.8rem;
          }
        `}</style>
      </div>
    </>
  );
};

export default CapmEligibilityModal;